import React, { useState, FC, KeyboardEvent } from 'react';
import { HiPlus, HiCheck, HiX } from 'react-icons/hi';
import Chip from '../Chip';

interface NewKeyWordProps {
  key_words: string[];
  setKeys: (keys: string[]) => void;
}

const NewKeyWord: FC<NewKeyWordProps> = ({ key_words, setKeys }) => {
  const [newWord, setNewWord] = useState<string>('');
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState<number>(-1);

  const handleClick = (word: string, idx: number) => {
    setNewWord(word);
    setEditIndex(idx);
    setIsEdit(true);
  };

  const cancelEdit = () => {
    setIsEdit(false);
    setEditIndex(-1);
    setNewWord('');
  };

  const handleUpdate = () => {
    if (!newWord.trim()) return;
    const updatedWords = [...key_words];
    updatedWords[editIndex] = newWord.trim();
    setKeys(updatedWords);
    cancelEdit();
  };

  const handleAdd = () => {
    if (!newWord.trim()) return;
    if (key_words.includes(newWord.trim())) {
        // Optional: show toast or shake? For now just ignore duplicates or add them
    }
    setKeys([...key_words, newWord.trim()]);
    setNewWord('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isEdit) handleUpdate();
      else handleAdd();
    }
    if (e.key === 'Escape') {
        if (isEdit) cancelEdit();
    }
  };

  const deleteRow = (e: any, idx: number) => {
    e.stopPropagation();
    const updatedWords = key_words.filter((_, i) => i !== idx);
    setKeys(updatedWords);
    if (isEdit && idx === editIndex) {
        cancelEdit();
    }
  };

  const inputClasses = 'flex-1 bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:ring-0 p-0';

  return (
    <div className='w-full'>
      <label className='block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2'>
        Focus Keywords
      </label>
      
      <div className='flex flex-wrap gap-2 items-center min-h-[46px] w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus-within:ring-2 focus-within:ring-primary-500/20 focus-within:border-primary-500 transition-all'>
        {key_words.map((word, idx) => (
          <div key={word} className={`${isEdit && editIndex === idx ? 'opacity-50' : ''}`}>
             <Chip
                label={word}
                onClick={() => handleClick(word, idx)}
                onDelete={(e) => deleteRow(e, idx)}
             />
          </div>
        ))}
        
        <div className='flex items-center flex-1 min-w-[120px] gap-2'>
            <input
                className={inputClasses}
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isEdit ? 'Edit keyword...' : 'Add keyword...'}
            />
            
            {newWord && (
                <div className='flex items-center gap-1'>
                    {isEdit ? (
                         <>
                            <button
                                type='button'
                                onClick={handleUpdate}
                                className='p-1 text-green-600 hover:bg-green-50 rounded-full transition-colors'
                            >
                                <HiCheck className='w-4 h-4' />
                            </button>
                            <button
                                type='button'
                                onClick={cancelEdit}
                                className='p-1 text-red-500 hover:bg-red-50 rounded-full transition-colors'
                            >
                                <HiX className='w-4 h-4' />
                            </button>
                         </>
                    ) : (
                        <button
                            type='button'
                            onClick={handleAdd}
                            className='p-1 text-primary-600 hover:bg-primary-50 rounded-full transition-colors'
                        >
                            <HiPlus className='w-4 h-4' />
                        </button>
                    )}
                </div>
            )}
        </div>
      </div>
      <p className='text-xs text-gray-500 mt-1.5'>
          {isEdit ? 'Press Enter to save changes, Esc to cancel.' : 'Type a keyword and press Enter to add it. Click a tag to edit.'}
      </p>
    </div>
  );
};

export default NewKeyWord;
